import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import CheckRounded from '@mui/icons-material/CheckRounded';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import ArrowForward from '@mui/icons-material/ArrowForward';
import PersonPinOutlined from '@mui/icons-material/PersonPinOutlined';
import { Button, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import styles from './organisation-details-header.module.scss';

type Props = {
  hasErrors: boolean;
  viewingMode: ViewingMode;
  selectedNodeIsRoot: boolean;
  headerText?: string;
  breadCrumbElement?: ReactElement;
  isScrolling?: boolean;
  isSubmitting: boolean;
  showResidentSettingsNavigation: boolean;
  onCancelButtonClicked: () => void;
  onRemoveButtonClicked: () => void;
  onViewingModeChanged: (viewingMode: ViewingMode) => void;
  onResidentSettingsNavigation: () => void;
};

const OrganisationUnitDetailsHeader = (props: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('organisation');
  const { hasPermission } = usePermissionAuthorization();

  const creationMode = (
    <div className={styles.topContainer}>
      <div className={styles.actions}>
        {props.hasErrors && (
          <Typography variant="caption" className={styles.error}>
            {t('validationErrorsSummaryText')}
          </Typography>
        )}
        <div className={styles.actionsContainer}>
          <Button data-testid="cancelButton" variant="text" onClick={props.onCancelButtonClicked}>
            {t('cancelFormButton')}
          </Button>
          <Button
            data-testid="submitButton"
            startIcon={<CheckRounded />}
            variant="contained"
            type="submit"
            loading={props.isSubmitting}
            loadingPosition="start"
          >
            {t('saveFormButton')}
          </Button>
        </div>
      </div>
    </div>
  );

  const viewingMode = (
    <div className={styles.actionsContainer}>
      {!props.selectedNodeIsRoot && (
        <>
          {hasPermission(Permission.DeleteOrganisationUnit) && (
            <Button
              data-testid="removeButton"
              startIcon={<DeleteForeverOutlinedIcon />}
              variant="text"
              onClick={() => props.onRemoveButtonClicked()}
            >
              {t('removeFormButton')}
            </Button>
          )}
          {hasPermission(Permission.UpdateOrganisationUnit) && (
            <Button
              data-testid="editButton"
              startIcon={<ModeEditOutlineOutlinedIcon />}
              variant="text"
              onClick={() => props.onViewingModeChanged('editing')}
            >
              {t('editFormButton')}
            </Button>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className={`${styles.container} ${props.isScrolling && styles.isScrollig}`}>
      {(props.viewingMode === 'creation' || props.viewingMode === 'editing') && creationMode}
      {props.viewingMode === 'viewing' && viewingMode}
      <div className={styles.bottomContainer}>
        <div className={styles.titleRow}>
          <Typography
            variant="h4"
            data-testid="organisationDetailHeader"
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {props.viewingMode === 'creation' && t('organisationUnitHeaderTitle')}
            {(props.viewingMode === 'viewing' || props.viewingMode === 'editing') && props.headerText}
          </Typography>
          {props.showResidentSettingsNavigation && (
            <Tooltip title={t('residentSettingsNavigationButtonLabel')}>
              <Button
                aria-label={t('residentSettingsNavigationButtonLabel')}
                className={styles.residentSettingsNavigationButton}
                data-testid="residentSettingsNavigationButton"
                onClick={props.onResidentSettingsNavigation}
              >
                <PersonPinOutlined />
                <ArrowForward />
              </Button>
            </Tooltip>
          )}
        </div>
        {props.breadCrumbElement}
      </div>
    </div>
  );
};

export default OrganisationUnitDetailsHeader;
